import React, {
  useEffect,
  useCallback,
  useRef,
  useMemo,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import type { StreamingType, CommentFilterType } from "modules/streaming";
import { streamingActions } from "modules/streaming";
import { modalActions } from "modules/modal";
import type { Store } from "../../store";
import type { EventInfo } from "../../utility";
import type { Timestamp } from "@google-cloud/firestore";
import {
  updateFirestoreDoc,
  deleteFirestoreDoc,
  setFirestoreDocument,
} from "../../utility/firebase";
import { getCommentCollectionPath } from "utility/streaming";
import type { BanReason, UserMessage } from "@spwn/types/firebase/firestore";
import { StreamingCommentLayout } from "./StreamingCommentLayout";
import { useIsChatAdmin } from "./useIsChatAdmin";
import { applyBlockedComments } from "./applyBlockedComments";
import type { DisplayComment } from "./applyBlockedComments";
import { useBlockedUsers } from "../../features/blockedUsers/useBlockedUsers";
import { useBlockUser } from "../../features/blockedUsers/useBlockUser";
import { UndoToast } from "../../ui/UndoToast";
import firebase from "firebase/app";
import "firebase/firestore";
import { useI18n } from "hooks/i18n/i18n";
import { isPopOut } from "./enableCommentPopOut";

/**
 * このコンポーネントが player に求める最小の形。
 *
 * PlainVideoPlayer から Pick すると addEventListener が THEOplayer の全イベントを
 * 受けるジェネリックのまま入ってくる。すると listener の引数型が呼び出しごとに
 * 決まる型変数のままになり、実装側では書き込み位置で全イベント型の交差を要求される。
 * テストダブルは listener を保存できても引数を渡して発火できなくなる。
 * 購読するイベントと読む項目だけを自前で宣言して、具体型に閉じる。
 */
export type CommentPlayer = {
  currentTime: number;
  paused: () => boolean;
  seekable: () => { length: number; end: (index: number) => number };
  addEventListener: (
    type: "seeked" | "timeupdate",
    listener: (event: { currentTime: number }) => void
  ) => void;
  removeEventListener: (
    type: "seeked" | "timeupdate",
    listener: (event: { currentTime: number }) => void
  ) => void;
};

type Props = {
  eventStart: Timestamp;
  streamingType: StreamingType;
  player: CommentPlayer | null;
  eventInfo: EventInfo;
  mainVideoId: string; // !!! don't allow null !!!
  selectedCommentFilter: CommentFilterType;
  THEOPlayerElementHeight?: number;
  hasDvrStartTime: boolean;
  isFullScreen?: boolean;
  isPollingEnabled: boolean;
};

const StreamingCommentV2: React.FC<Props> = (props) => {
  const [isLive, setIsLive] = useState(true);
  const user = useSelector((state: Store) => state.auth.user);
  const displayComments = useSelector(
    (state: Store) => state.streaming.displayComments
  );
  const eventGoodsMap = useSelector(
    (state: Store) => state.ticket.eventGoodsMap
  );
  const isDarkModeEnabled = useSelector(
    (state: Store) => state.streaming.streamingSettings.isDarkModeEnabled
  );
  const commentShardId = useSelector(
    (state: Store) => state.streaming.commentShardId
  );

  const dispatch = useDispatch();
  const { t } = useI18n();

  const blockedUsersState = useBlockedUsers({ userId: user.uid });
  const { blockUser, unblockUser, tombstoneCommentIds } = useBlockUser({
    eventId: props.eventInfo._id,
  });
  const [blockedToastUser, setBlockedToastUser] = useState<
    { uid: string; name: string } | undefined
  >(undefined);
  // トーストは消えた後もアンマウントしないので、ブロックのたびに key を変えて
  // 作り直す。同じ相手に続けてブロックしても出し直せる。
  const [blockedToastVersion, setBlockedToastVersion] = useState(0);

  // playerのseekable rangeからisEdgeかどうかを計算する
  const calculateIsEdge = useCallback((player: CommentPlayer): boolean => {
    try {
      const seekable = player.seekable();
      if (seekable.length === 0) return true;

      const edge = seekable.end(0);
      const now = player.currentTime;
      // 10sは深い意味はない. 適宜調整可
      return Boolean(now >= edge - 10);
    } catch (error) {
      console.error("calculateIsEdge error:", error);
      return true;
    }
  }, []);

  const handleSeekEvents = useCallback(() => {
    console.log("handleSeekEvents called", props.isFullScreen);

    if (props.player === null) {
      return;
    }

    if (!isPopOut()) {
      const timeUpdateEventChannel = new BroadcastChannel("player-seeked");

      timeUpdateEventChannel.postMessage({
        eventId: props.eventInfo._id,
        currentTime: props.player.currentTime,
        mainVideoId: props.mainVideoId,
        eventStartTime: props.eventStart,
      });
      timeUpdateEventChannel.close();
    }

    let removeChannel = false;
    let isEdge = false;

    if (props.streamingType === "Live" && props.hasDvrStartTime) {
      const seekable = props.player.seekable();

      // seekable range が存在するか確認
      if (seekable.length === 0) {
        console.warn("No seekable range available");
        // isLiveをtrueのままにして、コメント入力を有効化
        setIsLive(true);
        dispatch(streamingActions.setIsCommentDisabled(false));
      } else {
        console.log("handleSeekEvents", props.mainVideoId);
        isEdge = calculateIsEdge(props.player);
        removeChannel = !isEdge && props.hasDvrStartTime;

        setIsLive(isEdge);

        if (props.eventInfo._id && props.isPollingEnabled) {
          if (isEdge) {
            dispatch(
              streamingActions.startCommentPolling.started({
                eventId: props.eventInfo._id,
                videoId: props.mainVideoId,
              })
            );
          } else {
            dispatch(streamingActions.stopCommentPolling());
          }
        }

        // hasDvrStartTime === true && isLive === false の時に、コメント入力をdisabledにする
        const isCommentDisabled = Boolean(props.hasDvrStartTime && !isEdge);
        console.log("isCommentDisabled:", isCommentDisabled);
        dispatch(streamingActions.setIsCommentDisabled(isCommentDisabled));
      }
    }

    dispatch(
      streamingActions.handleSeekEvents.started({
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        eventId: props.eventInfo._id!,
        videoId: props.mainVideoId,
        player: props.player,
        eventStart: props.eventStart,
        // 監視が外れる
        removeChannel,
        // 余分にコメントを取得するかどうか
        isEdge,
      })
    );
  }, [
    dispatch,
    props.mainVideoId,
    props.eventInfo._id,
    props.eventStart,
    props.player,
    props.hasDvrStartTime,
    props.streamingType,
    props.isFullScreen,
    props.isPollingEnabled,
    calculateIsEdge,
  ]);

  // ポップアウトしたタブの準備完了を検知して初期表示用に現在の再生時刻を送信する
  useEffect(() => {
    const popoutChannel = new BroadcastChannel("popout-ready");
    popoutChannel.onmessage = handleSeekEvents;
    return () => popoutChannel.close();
  }, [handleSeekEvents]);

  const fetchDisplayComments = useCallback(() => {
    if (props.streamingType === "Live" && (isLive || !props.hasDvrStartTime)) {
      if (props.eventInfo._id && props.isPollingEnabled) {
        dispatch(
          streamingActions.startCommentPolling.started({
            eventId: props.eventInfo._id,
            videoId: props.mainVideoId,
          })
        );
      } else {
        dispatch(
          streamingActions.fetchDisplayComments.started({
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            eventId: props.eventInfo._id!,
            videoId: props.mainVideoId,
          })
        );
      }
    } else {
      handleSeekEvents();
    }
  }, [
    isLive,
    dispatch,
    handleSeekEvents,
    props.hasDvrStartTime,
    props.mainVideoId,
    props.eventInfo._id,
    props.streamingType,
    props.isPollingEnabled,
  ]);

  const isChatAdmin = useIsChatAdmin(props.eventInfo._id);

  const banComment = useCallback(
    async (commentId: string, banReason: BanReason) => {
      const msgCollectionRef = getCommentCollectionPath(
        // @ts-expect-error TS2345
        props.eventInfo._id,
        props.mainVideoId,
        props.eventInfo?.streamingInfo?.optionSettings
          ?.isCommentShardingEnabled,
        commentShardId.value
      );
      const msgRef = `${msgCollectionRef}/${commentId}`;
      // console.info(msgRef)
      await updateFirestoreDoc(msgRef, { isBan: true, banReason });
    },
    [
      commentShardId,
      props.mainVideoId,
      props.eventInfo._id,
      props.eventInfo?.streamingInfo?.optionSettings?.isCommentShardingEnabled,
    ]
  );

  /**
   * 管理者だけがBan処理する
   */
  const banCommentAdminOnly = useCallback(
    async (commentId: string) => {
      if (!isChatAdmin) {
        return;
      }
      await banComment(commentId, "admin");
    },
    [banComment, isChatAdmin]
  );

  /**
   * コメントを物理削除する
   *
   * 削除の際にBAN処理を呼んでから削除している理由：
   * - onSnapshotでは削除されたことを検知はしない
   * - そのため、一度banすることで変更検知を走らせて、コメントを更新している
   * - その後時間をおいて物理削除する
   */
  const deleteComment = useCallback(
    async (commentId: string) => {
      await banComment(commentId, "delete");
      // delete from display
      dispatch(streamingActions.deleteDisplayComment(commentId));
      // 別タブにコメント削除を通知
      const deleteCommentChannel = new BroadcastChannel("delete-comment");
      deleteCommentChannel.postMessage(commentId);
      deleteCommentChannel.close();
      // delay deletion to detect baned in fetchBANComments
      window.setTimeout(() => {
        const msgCollectionRef = getCommentCollectionPath(
          // @ts-expect-error TS2345
          props.eventInfo._id,
          props.mainVideoId,
          props.eventInfo?.streamingInfo?.optionSettings
            ?.isCommentShardingEnabled,
          commentShardId.value
        );
        const msgRef = `${msgCollectionRef}/${commentId}`;
        // console.info(msgRef)
        deleteFirestoreDoc(msgRef);
      }, 5000);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      banComment,
      commentShardId,
      props.mainVideoId,
      props.eventInfo._id,
      props.eventInfo?.streamingInfo?.optionSettings?.isCommentShardingEnabled,
    ]
  );

  // seekイベントを監視してコメントを取得する
  useEffect(() => {
    if (props.isFullScreen) {
      return;
    }

    if (
      (props.streamingType === "VOD" || props.hasDvrStartTime) &&
      props.player
    ) {
      props.player.addEventListener("seeked", handleSeekEvents);
    }

    let seekedEventChannel: BroadcastChannel | null = null;

    if (props.streamingType === "VOD" && isPopOut()) {
      seekedEventChannel = new BroadcastChannel("player-seeked");
      seekedEventChannel.onmessage = (event) => {
        const { mainVideoId, currentTime, eventStartTime, eventId } =
          event.data;
        if (mainVideoId !== props.mainVideoId) {
          return;
        }
        dispatch(
          streamingActions.handleSeekEvents.started({
            eventId,
            videoId: mainVideoId,
            player: { currentTime },
            eventStart: eventStartTime,
            isEdge: false, // VODなのでisEdgeはfalse
          })
        );
      };
    }

    return () => {
      if (
        (props.streamingType === "VOD" || props.hasDvrStartTime) &&
        props.player
      ) {
        props.player.removeEventListener("seeked", handleSeekEvents);
      }
      seekedEventChannel?.close();
    };
  }, [
    props.player,
    props.streamingType,
    props.mainVideoId,
    props.hasDvrStartTime,
    props.isFullScreen,
    handleSeekEvents,
    dispatch,
  ]);

  // 初回レンダリング時にコメントを取得する
  useEffect(() => {
    if (commentShardId.type === "init") return;

    // フルスクリーン時は、すでに、存在するcomment componentにコメント取得を任せる. 2重取得防止のため
    if (props.isFullScreen) {
      return;
    }

    // playerからisEdgeを計算
    let isEdge = true;
    if (
      props.streamingType === "Live" &&
      props.hasDvrStartTime &&
      props.player
    ) {
      isEdge = calculateIsEdge(props.player);
    }

    if (isEdge) {
      fetchDisplayComments();
    } else {
      handleSeekEvents();
    }

    dispatch(
      streamingActions.fetchBANComments.started({
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        eventId: props.eventInfo._id!,
        videoId: props.mainVideoId,
      })
    );
    dispatch(
      streamingActions.fetchBanUserIds.started({
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        eventId: props.eventInfo._id!,
        videoId: props.mainVideoId,
      })
    );

    // unMount
    return () => {
      console.log("unMount StreamingComment");
      dispatch(streamingActions.stopCommentPolling());
    };
  }, [
    commentShardId,
    props.eventInfo._id,
    props.mainVideoId,
    props.streamingType,
    props.hasDvrStartTime,
    props.player,
    props.isFullScreen,
    handleSeekEvents,
    dispatch,
    fetchDisplayComments,
    calculateIsEdge,
  ]);

  // コメントのフィルタリングタイプに応じて表示コメントを更新する
  const filteredDisplayComments = useMemo<DisplayComment[]>(() => {
    if (blockedUsersState.status === "loading") return [];

    const blockedUserIds =
      blockedUsersState.status === "loaded"
        ? blockedUsersState.blockedUsers.map((blockedUser) => blockedUser.id)
        : [];

    const byFilterType = (() => {
      switch (props.selectedCommentFilter) {
        case "all":
          return displayComments;
        case "comment":
          return displayComments.filter((c) => !c.isSuperChat);
        case "gift":
          return displayComments.filter((c) => c.isSuperChat);
        case "none":
          return [];
        default:
          return displayComments;
      }
    })();
    return applyBlockedComments({
      comments: byFilterType,
      blockedUserIds,
      tombstoneCommentIds,
      selfUid: user.uid,
    });
  }, [
    displayComments,
    props.selectedCommentFilter,
    blockedUsersState,
    tombstoneCommentIds,
    user.uid,
  ]);

  const blockUserWithTombstone = useMemo(() => {
    if (blockUser === undefined) return undefined;

    return async (comment: Pick<UserMessage, "uid" | "name">) => {
      // uid が無いと書き込めない。ここで抜けないと、書き込みが起きていないのに
      // 完了トーストと「取り消す」が出る。
      if (!comment.uid) return;

      await blockUser(comment, filteredDisplayComments);
      setBlockedToastUser({ uid: comment.uid, name: comment.name ?? "" });
      setBlockedToastVersion((version) => version + 1);
    };
  }, [blockUser, filteredDisplayComments]);

  const handleUndoBlock = useCallback(async () => {
    if (!blockedToastUser || unblockUser === undefined) return;
    try {
      await unblockUser(blockedToastUser.uid);
    } catch {
      dispatch(
        modalActions.toggleError({
          msg: t("streaming.commentBlock.undoError"),
        })
      );
    }
  }, [blockedToastUser, dispatch, t, unblockUser]);

  // VOD時にコメントを再現する用の処理
  const previousTime = useRef(0);
  useEffect(() => {
    // only use in VOD

    console.log("useEffect for VOD comment replay", props.isFullScreen, isLive);

    if (
      props.streamingType === "Live" &&
      (isLive || !props.hasDvrStartTime || props.isFullScreen)
    ) {
      return;
    }

    const playerTimeUpdateHandler = (e: {
      currentTime: number;
      eventStartTime: Timestamp;
    }) => {
      const currentTime = Math.floor(e.currentTime);
      if (
        (!props.eventStart ||
          currentTime === previousTime.current ||
          props.player === null ||
          props.player.paused()) &&
        !isPopOut()
      ) {
        return;
      }

      previousTime.current = currentTime;

      dispatch(
        streamingActions.fetchDisplayCommentsByTimeUpdate.started({
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          eventId: props.eventInfo._id!,
          videoId: props.mainVideoId,
          currentTime,
          eventStart: e.eventStartTime,
        })
      );
    };

    const handler = (e: { currentTime: number }) => {
      const timeUpdateEventChannel = new BroadcastChannel("player-timeupdate");

      timeUpdateEventChannel.postMessage({
        currentTime: e.currentTime,
        mainVideoId: props.mainVideoId,
        eventStartTime: props.eventStart,
      });
      timeUpdateEventChannel.close();

      playerTimeUpdateHandler({
        currentTime: e.currentTime,
        eventStartTime: props.eventStart,
      });
    };

    let timeUpdateEventChannel: BroadcastChannel | null = null;

    if (isPopOut()) {
      // ポップアウト直後にコメントを同期するための処理
      const popoutChannel = new BroadcastChannel("popout-ready");
      popoutChannel.postMessage(null); // ポップアウトした事を通知
      popoutChannel.close();

      // ポップアウト元のプレイヤーと同期する
      timeUpdateEventChannel = new BroadcastChannel("player-timeupdate");
      timeUpdateEventChannel.onmessage = (event) => {
        const { currentTime, mainVideoId, eventStartTime } = event.data;
        if (mainVideoId !== props.mainVideoId) {
          return;
        }
        playerTimeUpdateHandler({ currentTime, eventStartTime });
      };
    } else if (props.player !== null) {
      // MEMO: ポップアウトウィンドウではplayerがnullなので、
      //       ポップアウト元での挙動のためにアーリーリターンせずにここで処理している
      props.player.addEventListener("timeupdate", handler);
    }

    return () => {
      if (props.player !== null) {
        props.player.removeEventListener("timeupdate", handler);
      }
      timeUpdateEventChannel?.close();
    };
  }, [
    isLive,
    props.streamingType,
    props.player,
    props.eventInfo._id,
    props.mainVideoId,
    props.eventStart,
    props.isFullScreen,
    props.hasDvrStartTime,
    dispatch,
  ]);

  // 別タブで削除されたコメントを反映する
  useEffect(() => {
    const deleteCommentChannel = new BroadcastChannel("delete-comment");
    deleteCommentChannel.onmessage = (event) => {
      const commentId = event.data;
      dispatch(streamingActions.deleteDisplayComment(commentId));
    };
    return () => {
      deleteCommentChannel.close();
    };
  }, [dispatch]);

  const banUser = useCallback(
    async (banTargetComment: Pick<UserMessage, "_id" | "uid">) => {
      if (!banTargetComment || !props.eventInfo._id) return;

      // ユーザーブロック
      const msgCollectionParentRef = getCommentCollectionPath(
        props.eventInfo._id,
        props.mainVideoId,
        props.eventInfo?.streamingInfo?.optionSettings
          ?.isCommentShardingEnabled,
        commentShardId.value
      ).slice(0, -5); // remove '/msgs'
      await setFirestoreDocument(msgCollectionParentRef, {
        banUserIds: firebase.firestore.FieldValue.arrayUnion(
          banTargetComment.uid
        ),
      });
    },
    [
      commentShardId,
      props.mainVideoId,
      props.eventInfo._id,
      props.eventInfo?.streamingInfo?.optionSettings?.isCommentShardingEnabled,
    ]
  );

  return (
    <StreamingCommentLayout
      player={props.player}
      eventStart={props.eventStart}
      streamingType={props.streamingType}
      eventInfo={props.eventInfo}
      eventGoodsMap={eventGoodsMap}
      displayComments={filteredDisplayComments}
      user={user}
      isChatAdmin={isChatAdmin}
      isDarkModeEnabled={isDarkModeEnabled}
      THEOPlayerElementHeight={props.THEOPlayerElementHeight}
      commentFilterState={props.selectedCommentFilter}
      banUser={banUser}
      banComment={banCommentAdminOnly}
      blockUser={blockUserWithTombstone}
      deleteComment={deleteComment}
      blockedUserToast={
        blockedToastUser && (
          <UndoToast
            key={blockedToastVersion}
            message={t("streaming.commentBlock.blocked", {
              name: blockedToastUser.name || t("streaming.commentBlock.noName"),
            })}
            actionLabel={t("streaming.commentBlock.undo")}
            onClickAction={handleUndoBlock}
            isDarkModeEnabled={isDarkModeEnabled}
          />
        )
      }
    />
  );
};

export default StreamingCommentV2;
